import { useOmniboxEnrichment } from "@/contexts/OmniboxEnrichmentContext.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { ImplicitContextChip } from "@/components/messages/Omnibox/ImplicitContextChip.tsx";
import type { CalendarContext } from "@/types/threadEvents.ts";
import { useCallback, useEffect, useRef, useState } from "react";
import { format, isToday, isSameWeek } from "date-fns";
import { getDateRange } from "./dateRange.ts";
import type { ViewType } from "./types.ts";

const ENRICHMENT_ID = "calendar-view-context";

/** Format the chip label based on view type and current date. */
export function formatImplicitContextLabel(viewType: ViewType, currentDate: Date): string {
  switch (viewType) {
    case "day":
    case "multi-day": {
      // Multi-day is a 2-day-grid layout choice — the chip still anchors on
      // the visible start date, same wording as single day.
      if (isToday(currentDate)) {
        return `Today: ${format(currentDate, "MMM d")}`;
      }
      return format(currentDate, "EEE, MMM d");
    }
    case "week": {
      const { start, end } = getDateRange(currentDate, "week");
      const label =
        start.getMonth() === end.getMonth()
          ? `${format(start, "MMM d")}–${format(end, "d")}`
          : `${format(start, "MMM d")}–${format(end, "MMM d")}`;
      if (isSameWeek(currentDate, new Date())) {
        return `This week: ${label}`;
      }
      return label;
    }
    case "month": {
      return format(currentDate, "MMMM yyyy");
    }
  }
}

/**
 * Build the implicit-view `calendarContext` payload. The server will fetch
 * events in the range and render the JSON the actor consumes — pre-BIG-4941
 * the client did that itself and the payload routinely blew past the wire
 * cap on busy month views.
 */
export function buildImplicitContextCalendarContext(
  instruction: string,
  viewType: ViewType,
  startISO: string,
  endISO: string,
  timezone: string,
): CalendarContext {
  return {
    kind: "viewContext",
    instruction: instruction.trim(),
    timezone,
    // `multi-day` is a mobile-only layout variant of the day view — the server
    // schema only knows day/week/month, and the actor doesn't need the 1- vs
    // 2-column distinction (the start/end ISOs already convey the range).
    viewType: viewType === "multi-day" ? "day" : viewType,
    startISO,
    endISO,
  };
}

/**
 * Registers an implicit calendar context enrichment into the OmniboxEnrichmentContext.
 * Shows a chip representing the current calendar view (day/week/month) with all events
 * in that range. Automatically hides when manual time range selections are active.
 */
export function useImplicitCalendarContext(
  viewType: ViewType,
  currentDate: Date,
  hasManualSelections: boolean,
) {
  const { register, unregister } = useOmniboxEnrichment();
  const { getUserTimezone } = useCurrentUserContext();
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissed state when the user navigates to a different date or switches views
  const prevDateRef = useRef(currentDate.toISOString());
  const prevViewRef = useRef(viewType);
  useEffect(() => {
    const dateISO = currentDate.toISOString();
    if (dateISO !== prevDateRef.current || viewType !== prevViewRef.current) {
      prevDateRef.current = dateISO;
      prevViewRef.current = viewType;
      setDismissed(false);
    }
  }, [currentDate, viewType]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  const active = !dismissed && !hasManualSelections;

  useEffect(() => {
    if (!active) {
      unregister(ENRICHMENT_ID);
      return;
    }

    const { start, end } = getDateRange(currentDate, viewType);
    const startISO = start.toISOString();
    const endISO = end.toISOString();
    const label = formatImplicitContextLabel(viewType, currentDate);

    register({
      id: ENRICHMENT_ID,
      // Ambient page state (the calendar's current view week), not an explicit
      // selection — so it belongs only to the calendar's own omnibox, never a
      // thread/biscuit composer that happens to share the page.
      scope: "page",
      renderContent: () => <ImplicitContextChip label={label} onDismiss={handleDismiss} />,
      buildMessage: (text: string) => {
        const calendarContext = buildImplicitContextCalendarContext(
          text,
          viewType,
          startISO,
          endISO,
          getUserTimezone(),
        );
        return { text, calendarContext };
      },
    });
  }, [active, currentDate, viewType, register, unregister, handleDismiss, getUserTimezone]);

  // Unregister on unmount
  useEffect(() => {
    return () => unregister(ENRICHMENT_ID);
  }, [unregister]);

  return { isActive: active };
}
